// src/utils/browserNotifications.js

// /**
//  * Browser Notifications attached to an existing WebSocket (WhatsApp-style).
//  * Pure JS, framework-agnostic.
//  *
//  * Usage:
//  *   const detach = attachBrowserNotificationsToSocket(socket, {
//  *     notifyWhenVisible: false,
//  *     titleFor: (msg) => (msg?.type || "Notification").replace(/_/g, " "),
//  *     bodyFor: (msg) => msg?.message || "",
//  *     onClick: (msg) => { /* open drawer/route */ },
//  *     icon: "/icons/icon-192.png",
//  *     throttleMs: 0,
//  *     dedupeMax: 200,
//  *   });
//  *
//  *   // later:
//  *   detach(); // cleanup
//  */

// /** @typedef {Object} Options
//  * @property {boolean} [notifyWhenVisible=false]  Show even when tab visible
//  * @property {(msg:any)=>boolean} [shouldNotify]  Filter; return false to skip
//  * @property {(msg:any)=>string}  [titleFor]      Title resolver
//  * @property {(msg:any)=>string}  [bodyFor]       Body resolver
//  * @property {(msg:any)=>void}    [onClick]       Called on toast click
//  * @property {string}  [icon="/icons/icon-192.png"] Icon URL
//  * @property {number}  [throttleMs=0]  Min ms between toasts
//  * @property {number}  [dedupeMax=200] Max IDs kept for dedupe (LRU-ish)
//  * @property {boolean} [silent=true]   Silent notification (no sound)
//  * @property {boolean} [requireInteraction=false] Require user to dismiss
//  */

const DEFAULTS = {
  notifyWhenVisible: false,
  shouldNotify: undefined,
  titleFor: undefined,
  bodyFor: undefined,
  onClick: undefined,
  icon: "/icons/icon-192.png",
  throttleMs: 0,
  dedupeMax: 200,
  silent: true,
  requireInteraction: false,
};

function canUseNotifications() {
  if (typeof window === "undefined") return false;
  const loc = window.location;
  return (
    "Notification" in window &&
    loc &&
    (loc.protocol === "https:" || loc.hostname === "localhost")
  );
}

/** Ask permission once (best-effort, non-blocking) */
function requestPermissionIfNeeded() {
  if (!canUseNotifications()) return;
  if (Notification.permission === "default") {
    try {
      Notification.requestPermission().catch(() => {});
    } catch (_) {}
  }
}

/** Derive a stable ID for dedupe */
function messageId(msg) {
  if (!msg || typeof msg !== "object") return String(Date.now());
  return (
    msg.id ||
    `${msg.type || "notif"}-${msg.timestamp || Date.now()}-${msg.message || ""}`
  );
}

/** Minimal LRU-ish set (cap by size) */
function sizedSetPush(set, value, max) {
  if (set.size >= max) {
    // remove first inserted (not strict LRU but good enough)
    const first = set.values().next().value;
    if (first !== undefined) set.delete(first);
  }
  set.add(value);
}

// /**
//  * Attach notifications to an existing WebSocket.
//  * @param {WebSocket} socket
//  * @param {Options} [options]
//  * @returns {{detach:()=>void, setOptions:(partial:Partial<Options>)=>void}}
//  */
export function attachBrowserNotificationsToSocket(socket, options = {}) {
  if (!socket || typeof socket.addEventListener !== "function") {
    console.warn("[browserNotifications] A valid WebSocket is required.");
    return { detach: () => {}, setOptions: () => {} };
  }

  /** @type {Options} */
  const opts = { ...DEFAULTS, ...options };

  requestPermissionIfNeeded();

  // Local state
  let lastShownAt = 0;
  const seenIds = new Set();

  function shouldShowNow(msg) {
    // Filter
    if (typeof opts.shouldNotify === "function" && !opts.shouldNotify(msg)) {
      return false;
    }
    // Background only by default
    if (!opts.notifyWhenVisible && !document.hidden) {
      return false;
    }
    // Throttle
    if (opts.throttleMs > 0) {
      const now = Date.now();
      if (now - lastShownAt < opts.throttleMs) return false;
      lastShownAt = now;
    }
    // Dedupe
    const id = messageId(msg);
    if (seenIds.has(id)) return false;
    sizedSetPush(seenIds, id, opts.dedupeMax);
    return true;
  }

  function resolveTitle(msg) {
    if (typeof opts.titleFor === "function") return opts.titleFor(msg);
    const t = (msg?.type || "Notification").replace(/_/g, " ");
    return t.toUpperCase();
  }

  function resolveBody(msg) {
    if (typeof opts.bodyFor === "function") return opts.bodyFor(msg);
    return msg?.message || "";
  }

  function showNotification(msg) {
    if (!canUseNotifications() || Notification.permission !== "granted") return;

    const id = messageId(msg);
    const title = resolveTitle(msg) || "Notification";
    const body = resolveBody(msg) || "";

    const n = new Notification(title, {
      body,
      icon: opts.icon,
      tag: id, // collapse duplicates with same id
      requireInteraction: !!opts.requireInteraction,
      silent: !!opts.silent,
    });

    n.onclick = () => {
      try {
        n.close();
      } catch (_) {}
      try {
        window.focus();
      } catch (_) {}
      if (typeof opts.onClick === "function") {
        try {
          opts.onClick(msg);
        } catch (_) {}
      }
    };
  }

  function handleMessage(event) {
    let data = null;
    try {
      data = JSON.parse(event.data);
    } catch (e) {
      // If your WS sometimes sends non-JSON, ignore gracefully
      // console.error("[browserNotifications] non-JSON message", e);
      return;
    }

    if (!shouldShowNow(data)) return;
    showNotification(data);
  }

  socket.addEventListener("message", handleMessage);

  function detach() {
    try {
      socket.removeEventListener("message", handleMessage);
    } catch (_) {}
  }

  function setOptions(partial) {
    if (!partial || typeof partial !== "object") return;
    Object.assign(opts, partial);
  }

  return { detach, setOptions };
}

// /**
//  * Convenience helper: if you *don’t* have the socket instance,
//  * but have a connector that returns it.
//  *
//  * @param {(user:any)=>WebSocket} connectWebSocket
//  * @param {any} user
//  * @param {Options} [options]
//  * @returns {{socket:WebSocket|null, detach:()=>void, setOptions:(partial:Partial<Options>)=>void}}
//  */
export function attachUsingConnector(connectWebSocket, user, options = {}) {
  if (typeof connectWebSocket !== "function") {
    console.warn("[browserNotifications] connectWebSocket(user) function required.");
    return { socket: null, detach: () => {}, setOptions: () => {} };
  }
  const socket = connectWebSocket(user);
  const ctl = attachBrowserNotificationsToSocket(socket, options);
  return { socket, ...ctl };
}
